import React, { useState } from 'react';

/**
 * BatchUploadStatus — calm progress + summary panel for the useBatchUpload
 * hook. Renders nothing when idle. Two modes:
 *
 *   1. Live progress strip (while uploading):
 *        "Uploading 142 / 186 · 3 failed"
 *      Subtle progress hairline below.
 *
 *   2. Final summary card (after batch resolves):
 *        "179 images uploaded.  ·  7 failed."
 *        [ Show details ▾ ]
 *        — expanded list of failed filenames + reason + size
 *        [ Retry failed ]  [ Dismiss ]
 *
 * Tone: editorial — soft cream backgrounds, hairline borders, italic
 * Cormorant for diagnostics, never red. Mirrors the same data-testid
 * pattern used elsewhere.
 */
const BatchUploadStatus = ({ state, onRetryFailed, onDismiss, label = 'images', compact = false, t = null }) => {
  const [expanded, setExpanded] = useState(false);

  if (!state || (state.total === 0 && !state.summaryOpen)) return null;

  const { uploading, total, doneCount, failedCount, remainingCount, summaryOpen, items } = state;
  const progressPct = total > 0 ? Math.round(((doneCount + failedCount) / total) * 100) : 0;
  const failedItems = (items || []).filter((i) => i.status === 'failed');

  // Tiny interpolation helper so we can reuse i18n keys with placeholders.
  const fmt = (s, vars = {}) => Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

  // English fallbacks keep existing callers (ClientView, BoardEditor)
  // working unchanged when no `t` is supplied.
  const L = {
    uploading: t?.batch_uploading || 'Uploading',
    xOfY: t?.batch_x_of_y || '{done} of {total}',
    nFailed: t?.batch_n_failed || '{count} failed',
    nRemaining: t?.batch_n_remaining || '{count} remaining\u2026',
    finishing: t?.batch_finishing || 'Finishing up\u2026',
    complete: t?.batch_upload_complete || 'Upload complete',
    finished: t?.batch_upload_finished || 'Upload finished',
    dismissAria: t?.batch_dismiss_aria || 'Dismiss',
    uploadedOne: t?.batch_n_uploaded_one || `{count} ${label} uploaded.`,
    uploadedMany: t?.batch_n_uploaded_many || `{count} ${label}s uploaded.`,
    noneUploaded: t?.batch_no_uploaded || `No ${label}s uploaded.`,
    nFailedText: t?.batch_n_failed_text || '{count} failed.',
    genericError: t?.batch_generic_error || 'A few files couldn\u2019t be uploaded. You can review and retry the ones you\u2019d like to keep.',
    showDetails: t?.batch_show_details || 'Show details \u25be',
    hideDetails: t?.batch_hide_details || 'Hide details \u25b4',
    more: t?.batch_more || '+ {count} more',
    retry: t?.batch_retry_failed || 'Retry failed',
    close: t?.batch_close || 'Close',
    dismiss: t?.batch_dismiss || 'Dismiss',
    failedFallback: t?.batch_failed_fallback || 'failed',
    uploadFailedFallback: t?.batch_upload_failed_fallback || 'Upload failed',
  };

  // Group failures by code so the studio sees patterns instantly
  // (e.g. "10 of 12 failures: max per board reached").
  const grouped = {};
  failedItems.forEach((it) => {
    const k = it.errorCode || 'unknown';
    grouped[k] = grouped[k] || { count: 0, sample: it.errorMessage || L.uploadFailedFallback };
    grouped[k].count += 1;
  });

  const containerStyle = {
    background: 'var(--kahla-white)',
    border: '1px solid var(--kahla-border)',
    padding: compact ? '14px 18px' : '18px 22px',
    margin: '12px 0',
    fontSize: '13px',
    color: 'var(--kahla-text)',
  };

  // ── Live progress strip ────────────────────────────────────────────
  if (uploading) {
    return (
      <div style={containerStyle} data-testid="batch-upload-progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
          <span style={{
            fontSize: '10px',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
          }}>
            {L.uploading}
          </span>
          <span
            style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontSize: '14px', color: 'var(--kahla-text-light)' }}
            data-testid="batch-upload-counter"
          >
            {fmt(L.xOfY, { done: doneCount, total })}
            {failedCount > 0 && (
              <span style={{ color: 'var(--kahla-text-light)' }}>
                {' '} · {fmt(L.nFailed, { count: failedCount })}
              </span>
            )}
          </span>
        </div>
        {/* Hairline progress bar */}
        <div style={{ position: 'relative', height: '2px', background: 'var(--kahla-cream-light)', overflow: 'hidden' }}>
          <div
            style={{
              position: 'absolute',
              top: 0, left: 0, bottom: 0,
              width: `${progressPct}%`,
              background: 'var(--kahla-charcoal)',
              transition: 'width 0.3s ease-out',
            }}
            data-testid="batch-upload-progress-bar"
          />
        </div>
        <p style={{
          margin: '10px 0 0',
          fontSize: '11px',
          letterSpacing: '0.04em',
          color: 'var(--kahla-text-light)',
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: 'italic',
        }}>
          {remainingCount > 0 ? fmt(L.nRemaining, { count: remainingCount }) : L.finishing}
        </p>
      </div>
    );
  }

  // ── Final summary card ─────────────────────────────────────────────
  if (!summaryOpen || total === 0) return null;

  const allOk = failedCount === 0;
  const anyOk = doneCount > 0;

  return (
    <div style={containerStyle} data-testid="batch-upload-summary">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
        <span style={{
          fontSize: '10px',
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
        }}>
          {allOk ? L.complete : L.finished}
        </span>
        <button
          onClick={onDismiss}
          data-testid="batch-upload-dismiss-btn"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            color: 'var(--kahla-text-light)',
            lineHeight: 1,
            padding: 0,
          }}
          aria-label={L.dismissAria}
        >
          ×
        </button>
      </div>

      <p style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: '17px',
        color: 'var(--kahla-text)',
        margin: '0 0 6px',
        lineHeight: 1.4,
      }}
      data-testid="batch-upload-summary-headline"
      >
        {anyOk && (
          <>
            {fmt(doneCount === 1 ? L.uploadedOne : L.uploadedMany, { count: doneCount })}
          </>
        )}
        {!anyOk && (<>{L.noneUploaded}</>)}
        {!allOk && (
          <span style={{ color: 'var(--kahla-text-light)' }}>
            {' · '}
            {fmt(L.nFailedText, { count: failedCount })}
          </span>
        )}
      </p>

      {!allOk && (
        <p style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: 'italic',
          fontSize: '14px',
          color: 'var(--kahla-text-light)',
          margin: '0 0 16px',
          lineHeight: 1.5,
        }}>
          {Object.keys(grouped).length === 1
            ? Object.values(grouped)[0].sample
            : L.genericError}
        </p>
      )}

      {/* Expandable details — failed filenames + reasons */}
      {failedItems.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <button
            onClick={() => setExpanded((p) => !p)}
            data-testid="batch-upload-toggle-details"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontSize: '10px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              fontFamily: 'inherit',
            }}
          >
            {expanded ? L.hideDetails : L.showDetails}
          </button>
          {expanded && (
            <div
              style={{
                marginTop: '10px',
                maxHeight: '180px',
                overflowY: 'auto',
                background: 'var(--kahla-cream-light)',
                border: '1px solid var(--kahla-border)',
                padding: '10px 12px',
              }}
              data-testid="batch-upload-details"
            >
              {failedItems.slice(0, 200).map((it) => (
                <div
                  key={it.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    fontSize: '11px',
                  }}
                >
                  <span
                    title={it.filename}
                    style={{
                      fontFamily: 'Menlo, Consolas, monospace',
                      color: 'var(--kahla-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {it.filename}
                  </span>
                  <span style={{
                    color: 'var(--kahla-text-light)',
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}>
                    {it.errorMessage || it.errorCode || L.failedFallback}
                  </span>
                </div>
              ))}
              {failedItems.length > 200 && (
                <p style={{
                  margin: '6px 0 0',
                  fontSize: '10px',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'var(--kahla-text-light)',
                }}>
                  {fmt(L.more, { count: failedItems.length - 200 })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        {failedCount > 0 && (
          <button
            className="btn-primary"
            onClick={onRetryFailed}
            data-testid="batch-upload-retry-btn"
            style={{ fontSize: '11px', padding: '10px 22px' }}
          >
            {L.retry}
          </button>
        )}
        <button
          className="btn-secondary"
          onClick={onDismiss}
          data-testid="batch-upload-summary-dismiss-btn"
          style={{ fontSize: '11px', padding: '10px 22px' }}
        >
          {allOk ? L.close : L.dismiss}
        </button>
      </div>
    </div>
  );
};

export default BatchUploadStatus;
